/**
 * Mobile Menu Component
 * Extracted from Header.js
 * Shows sign-in / user card at bottom when menu is open on mobile
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { SHOW_PRODUCTS_PAGE, SHOW_SERVICES_PAGE, SHOW_REFERRAL_SYSTEM } from '../../../config/featureFlags';
import { useTranslation } from '../../../context/LanguageContext';

const MobileMenu = ({
  isMobileMenuOpen,
  mobileWebsiteExpanded,
  setMobileWebsiteExpanded,
  mobileServicesExpanded,
  setMobileServicesExpanded,
  mobileMoreInfoExpanded,
  setMobileMoreInfoExpanded,
  handleLinkClick,
  user,
  authLoading,
  avatarError,
  setAvatarError,
  getUserDisplayName,
  handleGoogleSignIn,
  handleLogout,
  isSalesPartner = false,
  isWebsiteOwner = false
}) => {
  const { t } = useTranslation();
  return (
    <div className={`mobile-menu-content ${isMobileMenuOpen ? 'open' : ''}`}>
      {/* Navigation items first */}
      <Link to="/" onClick={handleLinkClick} className="nav-link mobile-menu-item">
        <span>{t('nav.home')}</span>
      </Link>

      {SHOW_PRODUCTS_PAGE && (
        <Link to="/products" onClick={handleLinkClick} className="nav-link mobile-menu-item">
          <span>{t('nav.products')}</span>
        </Link>
      )}

      {/* WEBSITE - Collapsible */}
      <div className="mobile-menu-group">
        <button
          className="mobile-menu-group-header"
          onClick={() => setMobileWebsiteExpanded(!mobileWebsiteExpanded)}
          aria-expanded={mobileWebsiteExpanded}
        >
          <span>{t('nav.website')}</span>
          <svg
            className={`mobile-menu-chevron ${mobileWebsiteExpanded ? 'expanded' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className={`mobile-menu-submenu ${mobileWebsiteExpanded ? 'expanded' : ''}`}>
          <Link to="/website" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.overview')}</span>
          </Link>
          <Link to="/website/essential" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.essential')}</span>
          </Link>
          <Link to="/website/professional" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.professional')}</span>
          </Link>
          <Link to="/website/business-plus" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.businessPlus')}</span>
          </Link>
          <Link to="/website/business-elite" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.businessElite')}</span>
          </Link>
          <Link to="/website/enterprise" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.enterprise')}</span>
          </Link>
        </div>
      </div>

      {/* SERVICES - Collapsible (hidden when SHOW_SERVICES_PAGE is false) */}
      {SHOW_SERVICES_PAGE && (
        <div className="mobile-menu-group">
          <button
            className="mobile-menu-group-header"
            onClick={() => setMobileServicesExpanded(!mobileServicesExpanded)}
            aria-expanded={mobileServicesExpanded}
          >
            <span>{t('nav.services')}</span>
            <svg
              className={`mobile-menu-chevron ${mobileServicesExpanded ? 'expanded' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className={`mobile-menu-submenu ${mobileServicesExpanded ? 'expanded' : ''}`}>
            <Link to="/services/aiml" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
              <span>{t('nav.aimlServices')}</span>
            </Link>
            <Link to="/services/business" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
              <span>{t('nav.businessSolutions')}</span>
            </Link>
          </div>
        </div>
      )}

      <Link to="/blogs" onClick={handleLinkClick} className="nav-link mobile-menu-item">
        <span>{t('nav.blogs')}</span>
      </Link>

      {/* MORE INFO - Collapsible */}
      <div className="mobile-menu-group">
        <button
          className="mobile-menu-group-header"
          onClick={() => setMobileMoreInfoExpanded(!mobileMoreInfoExpanded)}
          aria-expanded={mobileMoreInfoExpanded}
        >
          <span>{t('nav.moreInfo')}</span>
          <svg
            className={`mobile-menu-chevron ${mobileMoreInfoExpanded ? 'expanded' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className={`mobile-menu-submenu ${mobileMoreInfoExpanded ? 'expanded' : ''}`}>
          <Link to="/careers" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.careers')}</span>
          </Link>
          <Link to="/faq" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.faqs')}</span>
          </Link>
          <Link to="/contact" onClick={handleLinkClick} className="nav-link mobile-menu-subitem">
            <span>{t('nav.contactUs')}</span>
          </Link>
        </div>
      </div>

      {/* Sign-in / User card - NOW AT BOTTOM */}
      <div className="mobile-menu-auth mobile-menu-auth-bottom">
        {!authLoading && (
          <>
            {!user ? (
              <button
                onClick={() => { handleGoogleSignIn(); handleLinkClick(); }}
                className="mobile-menu-google-signin"
                aria-label="Sign in with Google"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" className="google-icon">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Sign in with Google</span>
              </button>
            ) : (
              <div className="mobile-menu-user-block">
                <div className="mobile-menu-user-card">
                  <div className="user-avatar">
                    {user?.photoURL && !avatarError ? (
                      <img
                        src={user.photoURL}
                        alt={getUserDisplayName()}
                        className="avatar-image"
                        onError={() => setAvatarError(true)}
                        loading="lazy"
                      />
                    ) : (
                      <span className="avatar-initial">{getUserDisplayName().charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="user-info">
                    <span className="user-name-text">{getUserDisplayName()}</span>
                    {user?.email && (
                      <span className="user-email-text">{user.email}</span>
                    )}
                  </div>
                </div>
                <div className="mobile-menu-user-actions">
                  {user?.email === 'amit.mca.pu@gmail.com' && (
                    <Link
                      to="/admin-dashboard/call-requests/website-owner"
                      className="mobile-menu-user-action dashboard-menu-item"
                      onClick={handleLinkClick}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      <span>Admin Dashboard</span>
                    </Link>
                  )}
                  {isSalesPartner && (
                    <Link
                      to="/partner"
                      className="mobile-menu-user-action dashboard-menu-item"
                      onClick={handleLinkClick}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>Sales Partner Dashboard</span>
                    </Link>
                  )}
                  {isWebsiteOwner && (
                    <Link
                      to="/owner"
                      className="mobile-menu-user-action dashboard-menu-item"
                      onClick={handleLinkClick}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>Website Owner Dashboard</span>
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => { handleLogout(); handleLinkClick(); }}
                    className="mobile-menu-user-action logout-menu-item"
                    aria-label="Logout"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
